/**
 * Auth — every Firebase Auth call in the app.
 *
 * Screens call these instead of importing firebase/auth; each sign-in path
 * also makes sure the users/{email} doc exists (ensureUserDoc), which used
 * to be repeated in every handler.
 *
 * Functions throw Firebase errors as-is (they carry `.code`), so the auth
 * screen can map them to friendly copy.
 */

import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { deleteMyAccount, getFirebaseAuth, type DeleteAccountResult } from "@travelix/core";

import { type AuthUser } from "./auth-user";
import { unwrap } from "./result";
import { ensureUserDoc } from "./users";

export { displayNameOf, type AuthUser } from "./auth-user";

const MAGIC_LINK_EMAIL_KEY = "magicLinkEmail";

export function onAuthChange(callback: (user: AuthUser | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(getFirebaseAuth());
}

/**
 * Permanently delete the account and its data (server-side, see core's
 * deleteMyAccount), then drop the local session: the Auth user no longer
 * exists, so the cached token is useless. Throws if the server call fails;
 * the user stays signed in and can retry.
 */
export async function deleteAccount(): Promise<DeleteAccountResult> {
  const result = await unwrap(deleteMyAccount());
  await firebaseSignOut(getFirebaseAuth());
  return result;
}

// ─── Email + password ────────────────────────────────────────

export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  const { user } = await signInWithEmailAndPassword(getFirebaseAuth(), email.trim(), password);
  await ensureUserDoc(user);
  return user;
}

export async function signUpWithEmail(
  name: string,
  email: string,
  password: string,
): Promise<AuthUser> {
  const { user } = await createUserWithEmailAndPassword(getFirebaseAuth(), email.trim(), password);
  await updateProfile(user, { displayName: name.trim() });
  await ensureUserDoc(user, name.trim());
  return user;
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(getFirebaseAuth(), email.trim());
}

// ─── Magic link ──────────────────────────────────────────────

/** Email a sign-in link that returns to /auth, remembering the address. */
export async function sendMagicLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(getFirebaseAuth(), email, {
    url: `${window.location.origin}/auth`,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
}

export function isMagicLinkReturn(url: string): boolean {
  return isSignInWithEmailLink(getFirebaseAuth(), url);
}

/** The address sendMagicLink stashed on this device, if any. */
export function stashedMagicLinkEmail(): string | null {
  const stashed = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  return stashed && stashed.length > 0 ? stashed : null;
}

export async function completeMagicLink(email: string, url: string): Promise<AuthUser> {
  const { user } = await signInWithEmailLink(getFirebaseAuth(), email, url);
  window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  await ensureUserDoc(user);
  return user;
}

// ─── OAuth (Google / Apple) ──────────────────────────────────

export type OAuthProviderName = "google" | "apple";

/**
 * Native (Capacitor) vs web split. signInWithPopup only works in a real
 * browser — inside the Android WebView, Google rejects OAuth with
 * disallowed_useragent — so natively we go through the platform SDK and feed
 * its idToken into the Firebase JS SDK via signInWithCredential.
 */
export async function signInWithProvider(provider: OAuthProviderName): Promise<AuthUser> {
  const auth = getFirebaseAuth();
  let user: AuthUser;

  if (Capacitor.isNativePlatform()) {
    const result =
      provider === "google"
        ? await FirebaseAuthentication.signInWithGoogle()
        : await FirebaseAuthentication.signInWithApple();
    const idToken = result.credential?.idToken;
    if (!idToken) {
      // User cancelled the native sheet, or the plugin returned no idToken —
      // same code the popup path uses so the screen's error copy matches.
      throw Object.assign(new Error("Sign-in cancelled."), {
        code: "auth/popup-closed-by-user",
      });
    }
    const credential =
      provider === "google"
        ? GoogleAuthProvider.credential(idToken)
        : new OAuthProvider("apple.com").credential({ idToken });
    user = (await signInWithCredential(auth, credential)).user;
  } else {
    const popupProvider =
      provider === "google" ? new GoogleAuthProvider() : new OAuthProvider("apple.com");
    for (const scope of provider === "google" ? ["email", "profile"] : ["email", "name"]) {
      popupProvider.addScope(scope);
    }
    user = (await signInWithPopup(auth, popupProvider)).user;
  }

  await ensureUserDoc(user);
  return user;
}
